import type { AgentEvent } from '@lumlix/core/events';

/**
 * Streaming client for the segmented agent runtime.
 *
 * Supabase Edge Functions die at a 150s wall clock, so a long run arrives as a chain of
 * segments over one durable thread. The server ends a segment with a `continuation` event;
 * we immediately reopen. Conversation state lives in the Postgres checkpoint, so a
 * continuation carries only a runId — reconnects stay O(1) regardless of transcript length.
 *
 * The same path handles a dropped connection: an unfinished run whose stream ends without
 * `run_finished` is resumed exactly like a planned handoff.
 */

const SSE_DELIM = '\n\n';

export interface AgentClientConfig {
  baseUrl: string;
  getIdToken: () => Promise<string>;
  /** Bounded by the server's own segment cap; this is the client-side backstop. */
  maxSegments?: number;
}

export interface StartOptions {
  threadId: string;
  message: string;
  signal?: AbortSignal;
}

export type ConnectionState =
  | { phase: 'idle' }
  | { phase: 'streaming'; segment: number }
  /** Between segments. The UI shows continuity here, not a reconnect spinner. */
  | { phase: 'handoff'; segment: number }
  | { phase: 'retrying'; attempt: number; inMs: number }
  | { phase: 'done' }
  | { phase: 'failed'; error: string };

export interface RunHandle {
  events: AsyncIterable<AgentEvent>;
  abort: () => void;
}

class HttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

/** 429/5xx and network failures are worth retrying; 4xx client errors are not. */
function isRetryable(err: unknown): boolean {
  if (err instanceof HttpError) return err.status === 429 || err.status >= 500;
  return err instanceof TypeError; // fetch network failure
}

export class AgentClient {
  private readonly maxSegments: number;

  constructor(private readonly cfg: AgentClientConfig) {
    this.maxSegments = cfg.maxSegments ?? 24;
  }

  start(opts: StartOptions, onState?: (s: ConnectionState) => void): RunHandle {
    const ctrl = new AbortController();
    if (opts.signal) {
      opts.signal.addEventListener('abort', () => ctrl.abort(), { once: true });
    }
    return {
      abort: () => ctrl.abort(),
      events: this.run(opts, ctrl, onState),
    };
  }

  /** Resume a run the user navigated back to, or one that went headless. */
  resume(runId: string, onState?: (s: ConnectionState) => void): RunHandle {
    const ctrl = new AbortController();
    return {
      abort: () => ctrl.abort(),
      events: this.runFrom({ runId }, ctrl, onState, 0),
    };
  }

  approve(
    runId: string,
    approvalId: string,
    decision: 'approve' | 'reject',
    edits?: unknown,
    onState?: (s: ConnectionState) => void,
  ): RunHandle {
    const ctrl = new AbortController();
    return {
      abort: () => ctrl.abort(),
      events: this.runFrom(
        { runId, approvalId, decision, edits },
        ctrl,
        onState,
        0,
        '/agent/approve',
      ),
    };
  }

  private async *run(
    opts: StartOptions,
    ctrl: AbortController,
    onState?: (s: ConnectionState) => void,
  ): AsyncIterable<AgentEvent> {
    yield* this.runFrom({ threadId: opts.threadId, message: opts.message }, ctrl, onState, 0);
  }

  /**
   * Drives one run to completion across however many segments it takes.
   * `body` is the first request; every subsequent segment sends `{ runId }` only.
   */
  private async *runFrom(
    body: Record<string, unknown>,
    ctrl: AbortController,
    onState: ((s: ConnectionState) => void) | undefined,
    startSegment: number,
    path = '/agent/stream',
  ): AsyncIterable<AgentEvent> {
    let payload = body;
    let segment = startSegment;
    let runId: string | undefined = typeof body.runId === 'string' ? body.runId : undefined;
    let finished = false;
    let attempt = 0;

    while (!finished && segment < this.maxSegments) {
      if (ctrl.signal.aborted) return;
      onState?.({ phase: 'streaming', segment });

      let sawTerminal = false;
      let handedOff = false;

      try {
        for await (const ev of this.openSegment(path, payload, ctrl.signal)) {
          if (ev.type === 'run_started') runId = ev.runId;

          if (ev.type === 'continuation') {
            // Planned handoff. Swallow the event — it is transport, not conversation.
            runId = ev.runId;
            segment = ev.nextSegment;
            payload = { runId: ev.runId };
            handedOff = true;
            sawTerminal = true;
            onState?.({ phase: 'handoff', segment });
            break;
          }

          yield ev;

          if (ev.type === 'run_finished') {
            sawTerminal = true;
            finished = true;
            onState?.(
              ev.status === 'done'
                ? { phase: 'done' }
                : { phase: 'failed', error: ev.error ?? 'run failed' },
            );
            break;
          }

          // An approval parks the graph in Postgres. Nothing to reconnect to — the user
          // decides, and approve() opens a fresh stream later.
          if (ev.type === 'approval_required') {
            sawTerminal = true;
            finished = true;
            onState?.({ phase: 'idle' });
            break;
          }
        }
        attempt = 0;
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (!isRetryable(err) || attempt >= 4) {
          const msg = err instanceof Error ? err.message : String(err);
          onState?.({ phase: 'failed', error: msg });
          yield { type: 'error', message: msg, retryable: false };
          return;
        }
        // Exponential backoff, then resume the same run — never restart it.
        const delay = Math.min(2 ** attempt * 1000, 8000);
        attempt += 1;
        onState?.({ phase: 'retrying', attempt, inMs: delay });
        await sleep(delay, ctrl.signal);
        if (runId) payload = { runId };
        continue;
      }

      if (handedOff) continue;

      // Stream ended without a terminal event: the server was cut off mid-segment.
      // Treat it as an unplanned handoff and resume from the checkpoint.
      if (!sawTerminal) {
        if (!runId) {
          onState?.({ phase: 'failed', error: 'stream ended before run started' });
          yield { type: 'error', message: 'stream ended before run started', retryable: true };
          return;
        }
        segment += 1;
        payload = { runId };
        onState?.({ phase: 'handoff', segment });
      }
    }

    if (!finished && segment >= this.maxSegments) {
      const msg = `run exceeded ${this.maxSegments} segments`;
      onState?.({ phase: 'failed', error: msg });
      yield { type: 'error', message: msg, retryable: false };
    }
  }

  private async *openSegment(
    path: string,
    body: unknown,
    signal: AbortSignal,
  ): AsyncIterable<AgentEvent> {
    const token = await this.cfg.getIdToken();
    const res = await fetch(`${this.cfg.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      throw new HttpError(res.status, `agent ${res.status}: ${text.slice(0, 200)}`);
    }

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buf = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += value;

        let idx: number;
        while ((idx = buf.indexOf(SSE_DELIM)) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + SSE_DELIM.length);
          const ev = parseFrame(frame);
          if (ev) yield ev;
        }
      }
      const tail = parseFrame(buf);
      if (tail) yield tail;
    } finally {
      reader.cancel().catch(() => {});
    }
  }
}

function parseFrame(frame: string): AgentEvent | null {
  const line = frame.split('\n').find((l) => l.startsWith('data: '));
  if (!line) return null;
  try {
    return JSON.parse(line.slice(6)) as AgentEvent;
  } catch {
    return null;
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(t);
      resolve();
    }, { once: true });
  });
}
